/**
 * Application constants and configuration values
 * Centralizes magic numbers and commonly used values for maintainability
 */

// Visual Layout Constants
export const LAYOUT = {
  NODE_WIDTH: 360,
  NODE_BASE_HEIGHT: 48, // Base visual height for a compact single-row node (no description)
  DESCRIPTION_PREVIEW_HEIGHT: 20, // Extra height for the always-visible 1-line description preview
  COLUMN_SPACING: 60,
  ARCHIVED_GROUP_HEIGHT: 40,
  ARCHIVED_GAP: 10, // Gap between parent node and its archived group
  ARCHIVED_ITEM_HEIGHT: 16,
  TREE_PADDING: 10,
  MIN_TREE_HEIGHT: 500,

  DESCRIPTION_MAX_HEIGHT: 80, // CSS max-height on the expanded .description-text (overflow-y: auto)

  // Adaptive spacing configuration (true bottom-to-top visual gaps)
  MIN_GAP: 20,              // Minimum visual gap between any nodes / cross-family contour floor
  LEAF_SPACING: 20,         // Compact spacing between leaf nodes
  PARENT_SPACING: 30,       // Medium spacing for nodes with children
} as const;

// Animation and Transition Constants
export const ANIMATION = {
  ARROW_UPDATE_DELAY: 100,
  ZOOM_TENSION: 300,
  ZOOM_FRICTION: 30,
  TRANSITION_DURATION: 200,
} as const;

// Zoom Controls
export const ZOOM = {
  MIN: 0.1,
  MAX: 3.0,
  STEP: 1.2,
  DEFAULT: 1.0,
} as const;

// Root Node Identification
export const ROOT_NODE = {
  UUID: '00000000-0000-0000-0000-000000000000',
} as const;

// File Handling
export const FILES = {
  DEFAULT_SAMPLE_NAME: 'sample-todo-tree.tdl',
  DEFAULT_NEW_NAME: 'new-todo-list.tdl',
  JSON_INDENT_SPACES: 2,
  TDL_EXTENSION: '.tdl',
  JSON_EXTENSION: '.json',
} as const;

// Undo/Redo History
export const HISTORY = {
  MAX_ENTRIES: 100, // Cap on undo stack depth; oldest snapshots are dropped first
} as const;

// Time Estimate Configuration
export const TIME_ESTIMATE = {
  PRESETS: [0.1, 0.5, 1, 2, 4, 16] as const,
  LABELS: ['0.1h', '0.5h', '1h', '2h', '4h', '16h', 'Other'] as const,
  COLOR: '#6366f1', // Indigo color for time estimate badges
} as const;

// Day plan ("Today" list) and the daily review
export const TODAY = {
  // Past this many tasks the review warns, but never blocks: the point is to
  // make an oversized day visible, not to enforce a quota.
  SOFT_ITEM_LIMIT: 7,

  PANEL_TITLE: 'Today',
  PANEL_EMPTY: 'Nothing planned yet.',
  PLAN_BUTTON: 'Plan your day',
  REPLAN_BUTTON: 'Re-plan day',
  COLLAPSE_TOOLTIP: 'Collapse the Today panel',
  EXPAND_TOOLTIP: 'Expand the Today panel',
  REMOVE_TOOLTIP: 'Take off today (keeps the task)',
  MOVE_UP_TOOLTIP: 'Move up',
  MOVE_DOWN_TOOLTIP: 'Move down',
  FOCUS_TOOLTIP: 'Show this task in the tree',

  PROMPT_TITLE: 'New day',
  PROMPT_MESSAGE: 'Plan what you want to get done today?',
  PROMPT_DISMISS: 'Not now',

  REVIEW_TITLE: 'Plan your day',
  LEFTOVER_STEP_TITLE: 'Yesterday’s leftovers',
  LEFTOVER_STEP_HINT: 'Decide what to carry over. Dropping an item only takes it off the list — the task stays in your tree.',
  LEFTOVER_KEEP: 'Keep today',
  LEFTOVER_DEFER: 'Not today',
  LEFTOVER_DROP: 'Drop',
  LEFTOVER_COMPLETED: 'Finished last time',

  PICK_STEP_TITLE: 'Pick today’s tasks',
  PICK_SEARCH_PLACEHOLDER: 'Search tasks...',
  PICK_EMPTY: 'No matching tasks.',
  OVER_LIMIT_HINT: 'That’s a big day. You can still continue.',

  ORDER_STEP_TITLE: 'Put them in order',
  ORDER_STEP_HINT: 'Top of the list is what you start with.',
  ORDER_EMPTY: 'Nothing selected — you can finish with an empty day.',

  BACK: 'Back',
  NEXT: 'Next',
  FINISH: 'Finish',
} as const;

// UI Text and Messages
export const MESSAGES = {
  INVALID_ROOT: 'Could not find a valid root node named "Everything".',
  BLOCKED_COMPLETION: 'Cannot mark as complete. First complete these child tasks:',
  PARENTS_MARKED_INCOMPLETE: 'Parent tasks marked as incomplete:',
  DRAG_ACROSS_LAYERS: 'Cannot move across layers',
  ARCHIVE_TOOLTIP: 'Archive this task',
  DRAG_TOOLTIP: 'Drag to reorder, or onto another task to move it there',
  EDIT_NAME_TOOLTIP: 'Double-click to edit',
  EDIT_DESCRIPTION_TOOLTIP: 'Double-click to edit',
  DESCRIPTION_PLACEHOLDER: 'Enter description... (Ctrl+Enter to save, Esc to cancel)',
  EMPTY_DESCRIPTION: 'Add description...',
  CANNOT_ADD_CHILD_WITH_ESTIMATE: 'Cannot add child to a task with a time estimate. Remove the estimate first.',
  TIME_ESTIMATE_TOOLTIP: 'Click to set time estimate',
  TIME_ESTIMATE_SUM_TOOLTIP: 'Total time estimate from descendants',
  UNDO_TOOLTIP: 'Undo (Ctrl+Z)',
  REDO_TOOLTIP: 'Redo (Ctrl+Shift+Z)',
} as const;

// File System API Configuration
export const FILE_PICKER = {
  // For saving files - use TDL format
  TDL_TYPE: {
    description: 'Todo List Files',
    accept: { 'application/json': ['.tdl'] },
  },
  // For loading files - accept both formats for backwards compatibility
  LOAD_TYPES: {
    description: 'Todo List Files',
    accept: { 'application/json': ['.tdl', '.json'] },
  },
  // `accept` attribute for the native <input type="file"> load control.
  // Includes MIME types alongside the extensions so the OS file picker can
  // resolve them: a custom extension like `.tdl` has no registered type on
  // some platforms (notably macOS, which relies on UTIs), and listing only
  // unrecognized extensions causes the picker to grey out otherwise-valid
  // files — including the `.tdl` files this app itself saves. Pairing the
  // extensions with `application/json`/`text/plain` keeps those files
  // selectable so a saved file can always be reloaded. Selected files are
  // still content-validated by readTodoFile, so a permissive filter is safe.
  LOAD_ACCEPT: '.tdl,.json,application/json,text/plain',
  START_IN: 'downloads' as const,
} as const;

// Arrow Styling
// Arrow colors are CSS variable references; CustomArrow applies them via the
// SVG element's `style` so they re-resolve when the theme changes.
export const ARROW_STYLES = {
  ACTIVE: {
    color: 'var(--interactive-primary)',
    strokeWidth: 2,
    headSize: 6,
    curveness: 0.8,
  },
  ARCHIVED: {
    color: 'var(--state-archived)',
    strokeWidth: 1,
    headSize: 4,
    curveness: 0.3,
    dashness: { strokeLen: 5, nonStrokeLen: 5 },
  },
  // Priority-based arrow styling
  PRIORITY: {
    low: {
      color: 'var(--interactive-primary)',
      strokeWidth: 1.5,
      headSize: 5,
      opacity: 0.6,
    },
    medium: {
      color: 'var(--interactive-primary)',
      strokeWidth: 2,
      headSize: 6,
      opacity: 0.8,
    },
    high: {
      color: 'var(--priority-high)',
      strokeWidth: 3,
      headSize: 7,
      opacity: 0.9,
    },
    critical: {
      color: 'var(--priority-critical)',
      strokeWidth: 4,
      headSize: 8,
      opacity: 1.0,
    },
  },
} as const;

// Priority Icons and Colors
export const PRIORITY = {
  ICONS: {
    critical: '🔥',
    high: '⚡',
    medium: '⭐',
    low: '📝',
  },
  COLORS: {
    critical: '#dc2626',
    high: '#ea580c',
    medium: '#2563eb',
    low: '#65a30d',
  },
} as const;

// Status Icons
export const STATUS_ICONS = {
  DONE: '✅',
  BLOCKED: '⏸️',
  ARCHIVED: '📦',
  PENDING: '○',
} as const;

// UI Spacing and Layout
export const SPACING = {
  HEADER_GAP: 12,
  BUTTON_GAP: 8,
  STAT_GAP: 10,
  TOOLBAR_MARGIN: 10,
} as const;
